import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Shield,
  Package,
  Users,
  CheckCircle,
  XCircle,
  ClipboardList,
  TrendingUp,
  Loader2,
  Clock,
  AlertCircle,
  Download,
  FileSpreadsheet,
  BarChart3
} from "lucide-react";

const AdminPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, detailedRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/pending-products`, { withCredentials: true }),
        axios.get(`${API}/admin/detailed-stats`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setPendingProducts(pendingRes.data);
      setDetailedStats(detailedRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (productId, approved, rejectionReason = null) => {
    setProcessingId(productId);
    try {
      await axios.post(
        `${API}/admin/products/${productId}/validate`,
        { approved, rejection_reason: rejectionReason },
        { withCredentials: true }
      );
      toast.success(approved ? "Producto aprobado. Se ha notificado al usuario por email." : "Producto rechazado. Se ha notificado al usuario con el motivo.");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al procesar";
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_reutiliza.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exportación de ${type} completada`);
    } catch (error) {
      toast.error("Error al exportar los datos");
    } finally {
      setExporting(null);
    }
  };

  const conditionLabels = {
    nuevo: { label: "Nuevo", color: "bg-green-100 text-green-700" },
    bueno: { label: "Buen estado", color: "bg-blue-100 text-blue-700" },
    aceptable: { label: "Aceptable", color: "bg-yellow-100 text-yellow-700" }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#374B67] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#374B67]" data-testid="admin-title">
                Panel de Administración
              </h1>
              <p className="text-slate-600">Gestiona productos, usuarios y exporta datos</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto text-[#374B67] mb-2" />
              <p className="text-2xl font-bold text-[#374B67]">{stats?.total_users || 0}</p>
              <p className="text-xs text-slate-500">Usuarios</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <Package className="w-8 h-8 mx-auto text-[#374B67] mb-2" />
              <p className="text-2xl font-bold text-[#374B67]">{stats?.total_products || 0}</p>
              <p className="text-xs text-slate-500">Total Productos</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-yellow-50">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
              <p className="text-2xl font-bold text-yellow-700">{stats?.pending_products || 0}</p>
              <p className="text-xs text-yellow-600">Pendientes</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-green-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-700">{stats?.listed_products || 0}</p>
              <p className="text-xs text-green-600">Publicados</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-blue-50">
            <CardContent className="p-4 text-center">
              <ClipboardList className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-blue-700">{stats?.active_reservations || 0}</p>
              <p className="text-xs text-blue-600">Reservas Activas</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-[#2E7D32] mb-2" />
              <p className="text-2xl font-bold text-[#2E7D32]">{stats?.completed_deliveries || 0}</p>
              <p className="text-xs text-slate-500">Entregas</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="pending">
          <TabsList className="mb-6">
            <TabsTrigger value="pending" className="relative" data-testid="tab-pending">
              Pendientes de Validación
              {pendingProducts.length > 0 && (
                <Badge className="ml-2 bg-yellow-500 text-white">{pendingProducts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">
              Estadísticas
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">
              Exportar Datos
            </TabsTrigger>
          </TabsList>

          {/* Pending Products Tab */}
          <TabsContent value="pending">
            {pendingProducts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                  <h3 className="text-xl font-semibold text-slate-600 mb-2">Todo al día</h3>
                  <p className="text-slate-500">No hay productos pendientes de validación</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingProducts.map((product) => (
                  <Card key={product.product_id} className="overflow-hidden" data-testid={`pending-product-${product.product_id}`}>
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="w-full md:w-48 h-48 md:h-auto flex-shrink-0">
                        <img
                          src={product.image_url || "https://via.placeholder.com/200"}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Content */}
                      <CardContent className="p-5 flex-1">
                        <div className="flex flex-col h-full">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="text-xl font-semibold text-[#374B67]">{product.name}</h3>
                                <p className="text-sm text-slate-500">Por {product.owner_name}</p>
                              </div>
                              <Badge className={conditionLabels[product.condition]?.color}>
                                {conditionLabels[product.condition]?.label}
                              </Badge>
                            </div>
                            
                            <p className="text-slate-600 mb-4">{product.description}</p>
                            
                            <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                              <span className="px-2 py-1 bg-slate-100 rounded">{product.category}</span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {new Date(product.created_at).toLocaleDateString("es-ES")}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-3 pt-4 border-t border-slate-100">
                            <Button
                              onClick={() => handleValidation(product.product_id, true)}
                              disabled={processingId === product.product_id}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              data-testid={`approve-btn-${product.product_id}`}
                            >
                              {processingId === product.product_id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-2" />
                              )}
                              Aprobar y Publicar
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                const reason = prompt("Motivo del rechazo:");
                                if (reason) {
                                  handleValidation(product.product_id, false, reason);
                                }
                              }}
                              disabled={processingId === product.product_id}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`reject-btn-${product.product_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Users by Postal Code */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#374B67]">
                    <Users className="w-5 h-5" />
                    Usuarios por Código Postal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {detailedStats?.users_by_postal_code?.length > 0 ? (
                    <div className="space-y-3">
                      {detailedStats.users_by_postal_code.map((item) => (
                        <div key={item.postal_code} className="flex items-center justify-between">
                          <span className="text-slate-600">{item.postal_code}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#374B67] rounded-full"
                                style={{ width: `${(item.count / (detailedStats.users_by_postal_code[0]?.count || 1)) * 100}%` }}
                              />
                            </div>
                            <span className="font-semibold text-[#374B67] w-8 text-right">{item.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sin datos</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Contributors */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#374B67]">
                    <TrendingUp className="w-5 h-5" />
                    Top Contribuidores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {detailedStats?.top_contributors?.length > 0 ? (
                    <div className="space-y-3">
                      {detailedStats.top_contributors.map((contributor, index) => (
                        <div key={contributor.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? "bg-amber-100 text-amber-700" :
                              index === 1 ? "bg-slate-200 text-slate-600" :
                              index === 2 ? "bg-orange-100 text-orange-700" :
                              "bg-slate-100 text-slate-500"
                            }`}>
                              {index + 1}
                            </span>
                            <span className="text-slate-600">{contributor.name}</span>
                          </div>
                          <Badge className="bg-green-100 text-green-700">
                            {contributor.products} productos
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sin contribuidores aún</p>
                  )}
                </CardContent>
              </Card>

              {/* Products by Category */}
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#374B67]">
                    <BarChart3 className="w-5 h-5" />
                    Productos por Categoría
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {detailedStats?.by_category?.length > 0 ? (
                    <div className="space-y-3">
                      {detailedStats.by_category.map((cat) => {
                        const categoryNames = {
                          muebles: "Muebles",
                          electrodomesticos: "Electrodomésticos",
                          deporte: "Deporte",
                          juguetes: "Juguetes",
                          hogar: "Hogar",
                          otros: "Otros"
                        };
                        return (
                          <div key={cat.category} className="flex items-center justify-between">
                            <span className="text-slate-600">{categoryNames[cat.category] || cat.category}</span>
                            <span className="font-semibold text-[#374B67]">{cat.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sin productos</p>
                  )}
                </CardContent>
              </Card>

              {/* Environmental Impact */}
              <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-emerald-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <TrendingUp className="w-5 h-5" />
                    Impacto Ambiental
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Residuos evitados</span>
                      <span className="font-bold text-green-700">{detailedStats?.environmental_impact?.kg_waste_diverted || 0} kg</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">CO₂ ahorrado</span>
                      <span className="font-bold text-green-700">{detailedStats?.environmental_impact?.co2_saved_kg || 0} kg</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Árboles equivalentes</span>
                      <span className="font-bold text-green-700">{detailedStats?.environmental_impact?.trees_equivalent || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#374B67]">
                  <Download className="w-5 h-5" />
                  Exportar Datos (CSV)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-6">
                  Descarga los datos de la plataforma en formato CSV para análisis externos o reportes.
                </p>
                
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border border-slate-200 hover:border-[#374B67] transition-colors cursor-pointer" onClick={() => handleExport("users")}>
                    <CardContent className="p-6 text-center">
                      <Users className="w-10 h-10 mx-auto text-[#374B67] mb-3" />
                      <h4 className="font-semibold text-[#374B67] mb-1">Usuarios</h4>
                      <p className="text-xs text-slate-500 mb-4">Datos de todos los usuarios registrados</p>
                      <Button 
                        size="sm" 
                        className="w-full bg-[#374B67]"
                        disabled={exporting === "users"}
                        data-testid="export-users-btn"
                      >
                        {exporting === "users" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                        )}
                        Exportar
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border border-slate-200 hover:border-[#374B67] transition-colors cursor-pointer" onClick={() => handleExport("products")}>
                    <CardContent className="p-6 text-center">
                      <Package className="w-10 h-10 mx-auto text-[#374B67] mb-3" />
                      <h4 className="font-semibold text-[#374B67] mb-1">Productos</h4>
                      <p className="text-xs text-slate-500 mb-4">Listado completo de productos</p>
                      <Button 
                        size="sm" 
                        className="w-full bg-[#374B67]"
                        disabled={exporting === "products"}
                        data-testid="export-products-btn"
                      >
                        {exporting === "products" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                        )}
                        Exportar
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border border-slate-200 hover:border-[#374B67] transition-colors cursor-pointer" onClick={() => handleExport("reservations")}>
                    <CardContent className="p-6 text-center">
                      <ClipboardList className="w-10 h-10 mx-auto text-[#374B67] mb-3" />
                      <h4 className="font-semibold text-[#374B67] mb-1">Reservas</h4>
                      <p className="text-xs text-slate-500 mb-4">Historial de todas las reservas</p>
                      <Button 
                        size="sm" 
                        className="w-full bg-[#374B67]"
                        disabled={exporting === "reservations"}
                        data-testid="export-reservations-btn"
                      >
                        {exporting === "reservations" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                        )}
                        Exportar
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="border border-slate-200 hover:border-[#374B67] transition-colors cursor-pointer" onClick={() => handleExport("stats")}>
                    <CardContent className="p-6 text-center">
                      <BarChart3 className="w-10 h-10 mx-auto text-[#374B67] mb-3" />
                      <h4 className="font-semibold text-[#374B67] mb-1">Estadísticas</h4>
                      <p className="text-xs text-slate-500 mb-4">Resumen de métricas clave</p>
                      <Button 
                        size="sm" 
                        className="w-full bg-[#374B67]"
                        disabled={exporting === "stats"}
                        data-testid="export-stats-btn"
                      >
                        {exporting === "stats" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                        )}
                        Exportar
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Admin Emails Info */}
        <Card className="mt-8 border-0 shadow-sm bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium mb-1">Notificaciones por email</p>
                <p>Los usuarios reciben emails automáticos cuando sus productos son aprobados o rechazados (con el motivo del rechazo).</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminPage;
